// What each machine can actually make.
//
// Dictated by the factory, 2026-08-16. This is the seed; once the machine-admin
// screen exists these live in Mongo and this file is only the starting point.
//
// Two things shape the structure:
//
//   Capability is per PHASE, not per product. "L75 does conductor isolation for
//   any standard" is the real rule — listing products would mean editing every
//   machine each time a standard is added.
//
//   Capability has THREE levels, not two. The phrase "it can, but only in
//   extreme cases" came up for four different machines, and a planner that
//   cannot express it will happily route a 4 mm² rope onto SKIP2250 as though
//   that were normal.

export const LEVEL = {
  PRIMARY: "PRIMARY",     // what the machine is for
  SECONDARY: "SECONDARY", // does it properly, as routine work
  FALLBACK: "FALLBACK",   // physically possible, only when nothing else will do
};

export const LEVEL_RANK = { PRIMARY: 3, SECONDARY: 2, FALLBACK: 1 };

// null anywhere below means NOT KNOWN, never "no limit". The router reports it
// as something it could not check rather than quietly passing.
export const MACHINE_CAPABILITIES = [
  {
    machineId: "l75",
    name: "L75",
    description: "Insulation line. Also runs sheathing and winding work, but insulation is what it is for.",
    phases: [
      { phase: "conductor_isolation", level: LEVEL.PRIMARY },
      { phase: "interior_sheat", level: LEVEL.SECONDARY },
      { phase: "exterior_sheat", level: LEVEL.SECONDARY },
      { phase: "unwinding", level: LEVEL.SECONDARY },
      { phase: "rewinding", level: LEVEL.SECONDARY },
    ],
    payoff: { count: 1, maxDrumDiameter: 1250, maxWeightKg: 2000 },
    // Two take-ups, but only one runs at a time — the other is being changed
    // over. Recording 2 here would double this line's throughput in every
    // capacity calculation.
    takeUp: { count: 2, concurrent: 1, maxDrumDiameter: 1250, maxWeightKg: 2000 },
    // Goes down to 0.5 mm²: L75 and L70 are the two lines that can, and L75 is
    // the only one allowed to run MYYUP.
    limits: { sectionMin: 0.5, sectionMax: 35, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: 15 },
    products: null, // any standard
  },

  {
    machineId: "l70",
    name: "L70",
    description: "Insulation line for fine sections. Also unwinds ahead of pre-stranding.",
    phases: [
      { phase: "conductor_isolation", level: LEVEL.PRIMARY },
      { phase: "unwinding", level: LEVEL.SECONDARY },
    ],
    payoff: { count: 2, maxDrumDiameter: 1000, maxWeightKg: 1200 },
    takeUp: { count: 2, concurrent: 2, maxDrumDiameter: 1000, maxWeightKg: 2000 },
    limits: { sectionMin: 0.5, sectionMax: 4, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: 4 },
    // ASSUMPTION — stated as "CYYF, MYYM, ..." with the emphasis on "whatever
    // the conductor count, up to 4 mm²". Modelled as section-limited rather than
    // product-limited; needs confirming.
    products: null,
    // MYYUP runs on L75 only. Expressed as an exclusion rather than by turning
    // `products` into a list, because the rule really is "anything except this".
    productsExcluded: ["MYYUP"],
    assumptions: ["products: treated as any standard, limited by section ≤ 4 mm²"],
  },

  {
    machineId: "l120",
    name: "L120",
    description: "Main sheathing line. A tandem: extrudes both sheaths of a CYYF in a single pass.",
    phases: [
      { phase: "exterior_sheat", level: LEVEL.PRIMARY },
      { phase: "interior_sheat", level: LEVEL.SECONDARY },
      { phase: "rewinding", level: LEVEL.FALLBACK, note: "extreme cases only" },
    ],
    tandem: true,
    payoff: { count: 1, maxDrumDiameter: 2000, maxWeightKg: 10000 },
    // Two take-ups, both usable — unlike L75, where the second is only ever the
    // one being changed over.
    takeUp: { count: 2, concurrent: 2, maxDrumDiameter: 2000, maxWeightKg: 10000 },
    limits: { sectionMin: 1.5, sectionMax: 35, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: null },
    products: null,
  },

  {
    machineId: "skip2250",
    name: "SKIP2250",
    description: "Stranding line for heavy sections.",
    phases: [
      { phase: "rope", level: LEVEL.PRIMARY, sectionMin: 10, sectionMax: 35 },
      { phase: "rope", level: LEVEL.FALLBACK, sectionMin: 1.5, sectionMax: 10, note: "extreme cases only — DTB1250 is the machine for this range" },
    ],
    payoff: { count: 5, maxDrumDiameter: 1250, maxWeightKg: 4000 },
    takeUp: { count: 1, concurrent: 1, maxDrumDiameter: 2250, maxWeightKg: 10000 },
    limits: { sectionMin: 1.5, sectionMax: 35, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: null },
    products: null,
  },

  {
    machineId: "dtb1250",
    name: "DTB1250",
    description: "Stranding line for fine sections. Every copper rope starts here, and it is the only pre-stranding machine.",
    phases: [
      { phase: "rope", level: LEVEL.PRIMARY, sectionMin: 1.5, sectionMax: 10 },
      { phase: "rope", level: LEVEL.FALLBACK, sectionMin: 10, sectionMax: 35, note: "capable, but SKIP2250 is preferred above 10 mm²" },
      // All pre-stranding happens here, across the whole range — the 1.5-10
      // window is about stranding a finished conductor, where SKIP2250 takes
      // over above 10. Pre-stranding a 25 mm² conductor means twisting wires far
      // thinner than 25 mm²; the section names the finished product, not what
      // the machine is holding. Copper may be MULTI-WIRE or STRANDED.
      { phase: "pre_stranding", level: LEVEL.PRIMARY, sectionMin: 0.5, sectionMax: 35 },
    ],
    payoff: { count: 7, maxDrumDiameter: 1000, maxWeightKg: 1200 },
    // `drumDiameters` is the CLOSED list of what the take-up will physically
    // hold, not a ceiling. DTB1250 is built for one drum size and one only:
    // anything smaller does not seat, so "Ø1000 on dtb1250" is as wrong as
    // Ø1400 even though it is under the maximum. maxDrumDiameter alone read it
    // as an upper bound and let every smaller drum through silently.
    takeUp: { count: 2, concurrent: 2, maxDrumDiameter: 1250, drumDiameters: [1250], maxWeightKg: 4000 },
    limits: { sectionMin: 1.5, sectionMax: 35, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: null },
    products: null,
  },

  {
    machineId: "ach300",
    name: "ACH300",
    description: "Coiling machine for single-core cable.",
    phases: [{ phase: "coiling", level: LEVEL.PRIMARY }],
    payoff: { count: 1, maxDrumDiameter: 1250, maxWeightKg: 2000 },
    // No take-up: the output is a coil, not a drum. Absent by design, not unknown.
    takeUp: null,
    limits: { sectionMin: 1.5, sectionMax: 4, conductorsMin: 1, conductorsMax: 1, outerDiameterMax: null },
    products: ["MYF", "FY", "MYYUP"],
  },

  {
    machineId: "dhc600",
    name: "DHC600",
    description: "Coiling machine for multi-core cable. Every coil that is not single-core happens here.",
    phases: [{ phase: "coiling", level: LEVEL.PRIMARY }],
    payoff: { count: 1, maxDrumDiameter: 1600, maxWeightKg: 4000 },
    takeUp: null,
    limits: { sectionMin: 1.5, sectionMax: 6, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: null },
    products: null,
  },

  {
    machineId: "ath600",
    name: "ATH600",
    description: "Armouring line. The only machine that can armour, so every CYABYF passes through it.",
    phases: [
      { phase: "armouring", level: LEVEL.PRIMARY },
      { phase: "rewinding", level: LEVEL.SECONDARY },
    ],
    payoff: { count: 1, maxDrumDiameter: 1400, maxWeightKg: 4000 },
    takeUp: { count: 1, concurrent: 1, maxDrumDiameter: 1400, maxWeightKg: 4000 },
    limits: { sectionMin: 1.5, sectionMax: 35, conductorsMin: 1, conductorsMax: 5, outerDiameterMax: null },
    products: ["CYABYF"],
  },
];

export const machineById = (id) =>
  MACHINE_CAPABILITIES.find((m) => m.machineId === String(id).toLowerCase()) || null;
